/**
 * WHEN the handover sends itself, and what its result says. Pure.
 *
 * A timer in the BROWSER, like the e-commerce auto-sync and for the same reason:
 * no Azure timer on the $0 plan. It sends while somebody who turned it on has
 * i-SYNERGY open. OFF by default and per browser — it writes to another team's
 * database, so a person chooses to be the one doing it.
 */
export const HANDOVER_AUTO_INTERVAL_MS = 15 * 60_000;
export const HANDOVER_FIRST_DELAY_MS = 30_000;

export type HandoverAutoStatus = "ok" | "skipped" | "failed";

export interface IHandoverAutoResult {
  at: number;
  status: HandoverAutoStatus;
  message: string;
}

export function handoverDue(now: number, lastRunAt: number): boolean {
  if (!Number.isFinite(lastRunAt) || lastRunAt <= 0) return true;
  if (lastRunAt > now) return true;
  return now - lastRunAt >= HANDOVER_AUTO_INTERVAL_MS;
}

export function handoverNextDelay(now: number, lastRunAt: number): number {
  if (handoverDue(now, lastRunAt)) return HANDOVER_FIRST_DELAY_MS;
  return Math.max(HANDOVER_FIRST_DELAY_MS, lastRunAt + HANDOVER_AUTO_INTERVAL_MS - now);
}

export function handoverExplain(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  if (/consent|sign-in|monitor_window|refresh_token|interaction/i.test(raw)) {
    return "Their ERP sign-in has expired — press Grant access on a Fulfillment screen, then it carries on by itself.";
  }
  return raw.length > 220 ? `${raw.slice(0, 217)}…` : raw || "The send failed.";
}

const clock = (at: number): string =>
  new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** The one line on the Handover screen. Never hides a failure or a skip. */
export function describeHandoverAuto(
  enabled: boolean, running: boolean, r: IHandoverAutoResult | null
): string {
  if (!enabled) return "Automatic send is off in this browser.";
  if (running) return "Sending to their ERP now…";
  if (!r) return "Automatic send is on — first run shortly after opening, then every 15 minutes while i-Synergy is open.";
  const when = `Last run ${clock(r.at)}`;
  if (r.status === "ok") return `${when} — ${r.message}`;
  return `${when} — ${r.status === "skipped" ? "skipped" : "FAILED"}: ${r.message}`;
}
